import { useEffect, useState } from 'react';
import {
  DEFAULT_TEXT_MAX_LENGTH,
  DEFAULT_TEXTAREA_MAX_LENGTH,
  FIELD_DISPLAY_STYLE_LABELS,
  findAddedOtherOption,
  getAllowedDisplayStyles,
  optionsWithoutOther,
  OTHER_OPTION_LABEL,
  OTHER_TEXT_MAX_LENGTH,
  toggleOtherOption,
  withOtherOptionLast,
  resolveFieldDisplayStyle,
  resolveUseAsAnalysisAxis,
  type FieldDef,
  type FieldDisplayStyle,
  type FieldMode,
  type FieldOptionsMeta,
  type FieldType,
} from '@citizen-walk/shared-types';
import { Button } from '@citizen-walk/shared-ui';
import { RatingScaleEditor } from './RatingScaleEditor';
import {
  FIELD_MODE_COLORS,
  FIELD_MODE_LABELS,
  FIELD_TYPE_LABELS,
  parseOptionsLines,
  sortFieldsByDisplayOrder,
  stringifyOptionsLines,
  fieldKeyReferenceNote,
  isFieldDeletable,
  isFieldKeyFixed,
  type KeyRenameResult,
} from './dataSettingsHelpers';

/**
 * userFields / postFields 1 section 分の FieldDef 一覧 editor (Sprint BG-DataSettings 3)。
 *
 * 旧 v1 `DataSettingsPage.tsx` の `FieldSection` + `FieldRow` (L229-510) を踏襲。
 * - 行: 概要 (displayOrder/label/type chip + visible/分析軸/mode bar)
 * - 展開: 詳細 (label/key/type/description/definition + options or rating-scale)
 * - 新規追加 + 削除 + 内部キー変更。制限はシステムが内部キーで扱う項目だけ (isFieldDeletable /
 *   isFieldKeyFixed)、分析等が名前で読む項目は注意書きを出す (fieldKeyReferenceNote)
 * - 一覧は表示順 (displayOrder) の昇順で並べる。番号は入力を確定 (フォーカスを外す / Enter) した
 *   時点で反映し、行が並び替わる (入力途中で行が動かないように)
 */

export interface DataSettingsFieldListProps {
  title: string;
  source: 'userFields' | 'postFields';
  fields: FieldDef[];
  disabled?: boolean;
  onChange: (key: string, partial: Partial<FieldDef>) => void;
  /**
   * 内部キーのリネーム要求 (A-④ 退行修正)。空キー / 重複は呼び出し側で検証して
   * 結果を返す。成功時のみ親 state の `f.key` が更新される。
   */
  onRenameKey: (oldKey: string, newKey: string) => KeyRenameResult;
  onAdd: () => void;
  onRemove: (key: string) => void;
}

let uidSeq = 0;
function nextUid(): string {
  uidSeq += 1;
  return `dsf_${uidSeq}_${Math.random().toString(36).slice(2)}`;
}

export function DataSettingsFieldList({
  title,
  source,
  fields,
  disabled,
  onChange,
  onRenameKey,
  onAdd,
  onRemove,
}: DataSettingsFieldListProps) {
  // 各フィールドに対する安定 ID。`f.key` は内部キー編集で変化するため、React の
  // `key` や展開状態のキーには使えない (入力 1 文字ごとに FieldRow がリマウントされ
  // フォーカスとアコーディオンが閉じてしまう)。old2 の uidByKey 方式を踏襲。
  const [uidByKey, setUidByKey] = useState<Map<string, string>>(new Map());
  const [expandedUid, setExpandedUid] = useState<string | null>(null);

  // fields の構成が変わったら uid マップを更新 (既存キーの uid は維持)。
  useEffect(() => {
    setUidByKey((prev) => {
      const next = new Map<string, string>();
      let changed = false;
      for (const f of fields) {
        const existing = prev.get(f.key);
        if (existing) {
          next.set(f.key, existing);
        } else {
          next.set(f.key, nextUid());
          changed = true;
        }
      }
      // 削除等で size が縮んだ場合も差し替える。
      if (!changed && prev.size === next.size) return prev;
      return next;
    });
  }, [fields]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
      <h2 className="font-bold text-lg mb-4">{title}</h2>
      <div className="grid grid-cols-1 gap-2">
        {sortFieldsByDisplayOrder(fields).map((f) => {
          const uid = uidByKey.get(f.key) ?? f.key;
          return (
            <FieldRow
              key={uid}
              source={source}
              field={f}
              expanded={expandedUid === uid}
              disabled={disabled}
              onToggle={() => setExpandedUid(expandedUid === uid ? null : uid)}
              onChange={(p) => onChange(f.key, p)}
              onRenameKey={(newKey) => {
                const oldKey = f.key;
                const result = onRenameKey(oldKey, newKey);
                if (result.ok && newKey !== oldKey) {
                  // 親 state 内のキーが変わるので uid マップも付け替える。
                  setUidByKey((prev) => {
                    const map = new Map(prev);
                    const u = map.get(oldKey);
                    map.delete(oldKey);
                    if (u) map.set(newKey, u);
                    return map;
                  });
                }
                return result;
              }}
              deletable={isFieldDeletable(source, f.key)}
              keyFixed={isFieldKeyFixed(source, f.key)}
              referenceNote={fieldKeyReferenceNote(source, f.key)}
              onRemove={() => onRemove(f.key)}
            />
          );
        })}
      </div>
      <button
        type="button"
        onClick={onAdd}
        disabled={disabled}
        className="mt-3 w-full border-2 border-dashed border-slate-300 text-slate-600 py-3 rounded-lg hover:border-primary-400 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        + 項目を追加
      </button>
    </div>
  );
}

function FieldRow({
  source,
  field: f,
  expanded,
  disabled,
  onToggle,
  onChange,
  onRenameKey,
  deletable,
  keyFixed,
  referenceNote,
  onRemove,
}: {
  source: 'userFields' | 'postFields';
  field: FieldDef;
  expanded: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onChange: (p: Partial<FieldDef>) => void;
  onRenameKey: (newKey: string) => KeyRenameResult;
  deletable: boolean;
  keyFixed: boolean;
  referenceNote: string | null;
  onRemove: () => void;
}) {
  const axisDefault = resolveUseAsAnalysisAxis(f, source);
  const axisChecked = f.useAsAnalysisAxis ?? axisDefault;
  return (
    <div className="border border-slate-200 rounded-lg">
      {/* 概要行 */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50"
        onClick={onToggle}
      >
        <span className="text-xs text-slate-400 w-5">{expanded ? '▼' : '▶'}</span>
        {/* 表示順序 (折りたたみ時も表示) */}
        <DisplayOrderInput
          value={f.displayOrder}
          disabled={disabled}
          onCommit={(displayOrder) => onChange({ displayOrder })}
        />
        <span className="font-medium text-sm flex-1 truncate">{f.label}</span>
        <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
          {FIELD_TYPE_LABELS[f.type]}
        </span>
        {/* 投稿詳細表示トグル (折りたたみ時も表示) */}
        <label
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer select-none"
          title="投稿詳細などで表示する"
        >
          <input
            type="checkbox"
            checked={f.visible !== false}
            onChange={(e) => onChange({ visible: e.target.checked })}
            disabled={disabled}
            className="w-3 h-3"
          />
          詳細表示
        </label>
        {/* 分析の軸・凡例として使用 */}
        <label
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer select-none"
          title={`分析ツール (マトリクス / 地図) の軸候補・凡例として使用する。未設定の既定: ${
            axisDefault ? 'ON' : 'OFF'
          }`}
        >
          <input
            type="checkbox"
            checked={axisChecked}
            onChange={(e) => onChange({ useAsAnalysisAxis: e.target.checked })}
            disabled={disabled}
            className="w-3 h-3"
          />
          分析軸
        </label>
        <div className="flex gap-0.5">
          {(['required', 'optional', 'hidden'] as FieldMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange({ mode: m });
              }}
              disabled={disabled}
              className={`px-2 py-0.5 text-[10px] rounded text-white ${
                f.mode === m ? FIELD_MODE_COLORS[m] : 'bg-slate-200 text-slate-500'
              } disabled:opacity-50`}
            >
              {FIELD_MODE_LABELS[m]}
            </button>
          ))}
        </div>
      </div>

      {expanded && (
        <FieldRowDetail
          field={f}
          disabled={disabled}
          onChange={onChange}
          onRenameKey={onRenameKey}
          deletable={deletable}
          keyFixed={keyFixed}
          referenceNote={referenceNote}
          onRemove={onRemove}
        />
      )}
    </div>
  );
}

function FieldRowDetail({
  field: f,
  disabled,
  onChange,
  onRenameKey,
  deletable,
  keyFixed,
  referenceNote,
  onRemove,
}: {
  field: FieldDef;
  disabled?: boolean;
  onChange: (p: Partial<FieldDef>) => void;
  onRenameKey: (newKey: string) => KeyRenameResult;
  deletable: boolean;
  keyFixed: boolean;
  referenceNote: string | null;
  onRemove: () => void;
}) {
  // 内部キーは入力中ローカル draft に持ち、blur / Enter でのみ親へ commit する
  // (A-④ 退行修正)。1 文字ごとに親 state の `f.key` を書き換えると、行のリマウントや
  // フォーカス喪失を招くため。検証 NG なら draft を元に戻し inline エラーを表示する。
  const [keyDraft, setKeyDraft] = useState(f.key);
  const [keyError, setKeyError] = useState<string | null>(null);
  useEffect(() => {
    setKeyDraft(f.key);
    setKeyError(null);
  }, [f.key]);

  function commitKey() {
    if (keyDraft === f.key) {
      setKeyError(null);
      return;
    }
    const result = onRenameKey(keyDraft);
    if (result.ok) {
      setKeyError(null);
    } else {
      setKeyError(result.error);
      setKeyDraft(f.key);
    }
  }

  return (
    <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 grid grid-cols-1 gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">項目名（設問文）</span>
          <input
            value={f.label}
            onChange={(e) => onChange({ label: e.target.value })}
            disabled={disabled}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">内部キー</span>
          <input
            value={keyDraft}
            disabled={disabled || keyFixed}
            onChange={(e) => {
              setKeyDraft(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''));
              if (keyError) setKeyError(null);
            }}
            onBlur={commitKey}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            aria-invalid={keyError ? true : undefined}
            className={`w-full px-2 py-1.5 border rounded text-sm disabled:bg-slate-100 disabled:text-slate-500 ${
              keyError ? 'border-danger-400' : 'border-slate-300'
            }`}
          />
          {keyError && <span className="text-[10px] text-danger-600 block mt-1">{keyError}</span>}
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">データ型</span>
          <select
            value={f.type}
            onChange={(e) => onChange({ type: e.target.value as FieldType })}
            disabled={disabled}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
          >
            {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">補足説明</span>
          <input
            value={f.description}
            onChange={(e) => onChange({ description: e.target.value })}
            disabled={disabled}
            placeholder="入力欄の下に表示される説明文"
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-slate-600 block mb-1">
            定義（分析ツール凡例用）
          </span>
          <textarea
            value={f.definition ?? ''}
            onChange={(e) => onChange({ definition: e.target.value })}
            disabled={disabled}
            placeholder="分析ツールの軸選択メニューで表示される定義・説明文"
            rows={2}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </label>
      </div>

      {/* 選択肢 (select / multiselect のみ) */}
      {(f.type === 'select' || f.type === 'multiselect') && (
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">
            選択肢（1 行に 1 つ。各行は表示ラベルかつ保存値）
          </span>
          <OptionsLinesTextarea
            options={optionsWithoutOther(f)}
            onChange={(options) => onChange({ options: withOtherOptionLast(f, options) })}
            disabled={disabled}
          />
        </label>
      )}

      {/* 「その他」の追加と自由記述欄の有無 (管理者が明示的に設定、自動検出はしない) */}
      {(f.type === 'select' || f.type === 'multiselect') && (
        <div className="grid grid-cols-1 gap-1.5">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={findAddedOtherOption(f) !== null}
              onChange={(e) => onChange(toggleOtherOption(f, e.target.checked))}
              disabled={disabled}
              className="mt-0.5"
            />
            <span className="text-xs text-slate-700">
              「{OTHER_OPTION_LABEL}」を選択肢の最後に追加する
            </span>
          </label>
          {findAddedOtherOption(f) && (
            <label className="ml-6 flex items-start gap-2">
              <input
                type="checkbox"
                checked={f.otherTextInput !== false}
                onChange={(e) => onChange({ otherTextInput: e.target.checked })}
                disabled={disabled}
                className="mt-0.5"
              />
              <span className="text-xs text-slate-700">
                「{OTHER_OPTION_LABEL}」に自由記述欄を付ける
                <span className="block text-[10px] text-slate-500">
                  回答する人の画面で「{OTHER_OPTION_LABEL}」の後ろに、内容を書き込む欄が出ます（最大
                  {OTHER_TEXT_MAX_LENGTH} 字）。
                </span>
              </span>
            </label>
          )}
        </div>
      )}

      {/* 表示タイプ (docs/105 §① FieldDisplayStyle)。データ型で選択肢が動的に変化。
          単一スタイルしか無い型 (date/location/photo) では選びようがないので非表示。 */}
      <DisplayStyleSelect field={f} onChange={onChange} disabled={disabled} />

      {/* text / textarea: 文字数上限 (Sprint BG-DataSettings 27) */}
      {(f.type === 'text' || f.type === 'textarea') && (
        <label className="block">
          <span className="text-xs font-medium text-slate-600 block mb-1">文字数上限</span>
          <input
            type="number"
            min={1}
            step={1}
            value={f.optionsMeta?.maxLength ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              const prev: FieldOptionsMeta = f.optionsMeta ?? {};
              if (raw === '') {
                const { maxLength: _omit, ...rest } = prev;
                onChange({ optionsMeta: rest });
              } else {
                const parsed = parseInt(raw, 10);
                if (Number.isFinite(parsed) && parsed > 0) {
                  onChange({ optionsMeta: { ...prev, maxLength: parsed } });
                }
              }
            }}
            disabled={disabled}
            placeholder={
              f.type === 'text'
                ? String(DEFAULT_TEXT_MAX_LENGTH)
                : String(DEFAULT_TEXTAREA_MAX_LENGTH)
            }
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
          <span className="text-[10px] text-slate-500 block mt-1">
            空欄なら text={DEFAULT_TEXT_MAX_LENGTH} 字 / textarea={DEFAULT_TEXTAREA_MAX_LENGTH} 字
            を使用
          </span>
        </label>
      )}

      {/* rating-scale 固有 */}
      {f.type === 'rating-scale' && (
        <RatingScaleEditor field={f} onChange={onChange} disabled={disabled} />
      )}

      {referenceNote && (
        <p className="text-[10px] text-notice-700 bg-notice-50 rounded px-2 py-1">
          この項目は「{referenceNote}」が内部キー「{f.key}
          」で値を読みます。削除したり内部キーを変えたりすると、それらに表示されなくなります。
        </p>
      )}
      {deletable && keyFixed && (
        <p className="text-[10px] text-slate-500">
          参加者情報の専用欄に保存し、アンケート回答の参加者情報や分析の属性に使う項目のため、内部キーは変えられません（削除はできます）。
        </p>
      )}
      {deletable ? (
        <Button
          type="button"
          variant="danger"
          size="sm"
          onClick={onRemove}
          disabled={disabled}
          className="self-start"
        >
          この項目を削除
        </Button>
      ) : (
        <p className="text-[10px] text-slate-500">
          この項目は画面の本体が専用の欄で使うため、削除と内部キーの変更はできません。使わないときは「非表示」にしてください。
        </p>
      )}
    </div>
  );
}

/**
 * 「表示タイプ」ドロップダウン (docs/105 §① FieldDisplayStyle)。
 *
 * - 選択肢は `getAllowedDisplayStyles(field.type)` で型に応じて動的に変化する。
 * - 単一スタイルしか無い型 (date / location / photo) では選びようがないので何も描画しない。
 * - 現在値は `resolveFieldDisplayStyle` で解決 (top-level displayStyle 優先、
 *   未設定の select/multiselect は旧 optionsMeta.displayStyle を後方互換で吸収)。
 * - 保存先は top-level `field.displayStyle`。
 */
function DisplayStyleSelect({
  field: f,
  onChange,
  disabled,
}: {
  field: FieldDef;
  onChange: (p: Partial<FieldDef>) => void;
  disabled?: boolean;
}) {
  const allowed = getAllowedDisplayStyles(f.type);
  if (allowed.length <= 1) return null;
  const current = resolveFieldDisplayStyle(f);
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 block mb-1">表示タイプ</span>
      <select
        value={current}
        onChange={(e) => onChange({ displayStyle: e.target.value as FieldDisplayStyle })}
        disabled={disabled}
        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
      >
        {allowed.map((style) => (
          <option key={style} value={style}>
            {FIELD_DISPLAY_STYLE_LABELS[style]}
          </option>
        ))}
      </select>
      <span className="text-[10px] text-slate-500 block mt-1">
        入力フォームでこの項目をどう表示するか（同じデータ型でも見た目を選べます）
      </span>
    </label>
  );
}

/**
 * 選択肢の textarea (1 行に 1 つ)。
 *
 * 入力中の文字列は手元に持ち、保存値 (options) から毎回作り直さない。作り直すと
 * 末尾で押した改行 (空行) や行末の空白が parse で消え、末尾で改行できなくなる。
 * options が外から変わったとき (別の項目の表示に切り替わった等) だけ入力中の文字列を差し替える。
 */
function OptionsLinesTextarea({
  options,
  onChange,
  disabled,
}: {
  options: { value: string; label: string }[];
  onChange: (options: { value: string; label: string }[]) => void;
  disabled?: boolean;
}) {
  const external = stringifyOptionsLines(options);
  const [draft, setDraft] = useState(external);
  // effect を使わず描画中に同期する (React 推奨の「props の変化に合わせて state を調整」形)。
  if (stringifyOptionsLines(parseOptionsLines(draft)) !== external) setDraft(external);
  return (
    <textarea
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(parseOptionsLines(e.target.value));
      }}
      disabled={disabled}
      rows={Math.max(3, draft.split('\n').length + 1)}
      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm font-mono"
    />
  );
}

/**
 * 表示順の番号欄。入力中は手元の値だけ変え、確定 (フォーカスを外す / Enter) したときに反映する。
 * 反映すると一覧が番号順に並び替わるので、入力途中 (例: 「15」を打つ途中の「1」) で行が動かないようにする。
 */
function DisplayOrderInput({
  value,
  disabled,
  onCommit,
}: {
  value: number | undefined;
  disabled?: boolean;
  onCommit: (value: number | undefined) => void;
}) {
  const external = value === undefined ? '' : String(value);
  const [draft, setDraft] = useState(external);
  const [editing, setEditing] = useState(false);
  const shown = editing ? draft : external;
  function commit() {
    setEditing(false);
    const next = draft.trim() === '' ? undefined : Number(draft);
    if (next !== undefined && !Number.isFinite(next)) return;
    if (next !== value) onCommit(next);
  }
  return (
    <input
      type="number"
      step="1"
      value={shown}
      onFocus={() => {
        setDraft(external);
        setEditing(true);
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      disabled={disabled}
      placeholder="順"
      aria-label="表示順序"
      title="表示順序 (小さいほど先)。入力を確定すると一覧が番号順に並び替わります"
      className="w-12 px-1 py-0.5 border border-slate-300 rounded text-xs text-center"
    />
  );
}
